import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DataTable,
  Field,
  Input,
  Modal,
  StateBlock,
  useToast,
  type Column,
} from '@w7/ui'
import { useMemo, useState, type FormEvent } from 'react'
import { cmApi, type SecurityCheaterRow } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'
import { CheaterPlayerPicker } from './CheaterPlayerPicker'

/**
 * Lab `/all-cheaters` — Strix Security chrome.
 * Add = modal + player picker; Action Off = confirm remove (Lab confirm-cheat).
 */
export function AllCheatersPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [qDraft, setQDraft] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [player, setPlayer] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [pendingOff, setPendingOff] = useState<SecurityCheaterRow | null>(null)
  const pageSize = 50

  const invalidate = () => qc.invalidateQueries({ queryKey: ['cm', 'security', 'cheaters'] })

  const listQ = useQuery({
    queryKey: ['cm', 'security', 'cheaters', page, pageSize, q],
    queryFn: () => cmApi.securityCheatersList(authHeaders(), { page, pageSize, q: q || undefined }),
  })

  const addM = useMutation({
    mutationFn: () => {
      if (!player?.trim() || !reason.trim()) {
        throw new Error('Please select a valid player and enter a reason.')
      }
      return cmApi.securityCheatersSave(authHeaders(), {
        username: player.trim(),
        comment: reason.trim(),
        enabled: true,
      })
    },
    onSuccess: async () => {
      toast.success('Cheater saved')
      setAddOpen(false)
      setPlayer(null)
      setReason('')
      await invalidate()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  const removeM = useMutation({
    mutationFn: (row: SecurityCheaterRow) =>
      cmApi.securityCheatersSave(authHeaders(), {
        userId: row.userId ?? undefined,
        username: row.username,
        comment: row.comment ?? undefined,
        enabled: false,
      }),
    onSuccess: async (_res, row) => {
      toast.success(`Removed ${row.username} from cheater list`)
      setPendingOff(null)
      await invalidate()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  const rows = listQ.data?.rows ?? []
  const total = listQ.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const columns = useMemo((): Column<SecurityCheaterRow>[] => {
    return [
      {
        id: 'n',
        header: '#',
        width: 48,
        numeric: true,
        cell: (_row, i) => (page - 1) * pageSize + i + 1,
      },
      { id: 'player', header: 'Player', cell: (r) => r.username },
      { id: 'comment', header: 'Comment', cell: (r) => r.comment ?? '—' },
      {
        id: 'action',
        header: 'Action',
        cell: (r) => (
          <Button
            type="button"
            size="sm"
            variant="primary"
            busy={removeM.isPending && pendingOff?.id === r.id}
            aria-label={`Turn off cheater ${r.username}`}
            onClick={() => setPendingOff(r)}
          >
            On
          </Button>
        ),
      },
    ]
  }, [page, pageSize, pendingOff?.id, removeM.isPending])

  if (listQ.isLoading && !rows.length) {
    return (
      <StateBlock icon="settings" title="Loading All cheaters…">
        Reading CM-API security lists.
      </StateBlock>
    )
  }

  return (
    <CmPanel
      title="All cheaters"
      subtitle="Flag players for sniping, matching, or syndicate betting"
      actions={
        <Button
          type="button"
          variant="secondary"
          busy={listQ.isFetching}
          onClick={() => void listQ.refetch()}
        >
          Refresh
        </Button>
      }
    >
      <div className="cm-security">
        <form
          className="cm-toolbar"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            const next = qDraft.trim()
            if (!next) {
              toast.error('Please enter player name')
              return
            }
            setPage(1)
            setQ(next)
          }}
        >
          <div className="cm-toolbar-search">
            <Field label="Search">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={qDraft}
                  onChange={(e) => {
                    const v = e.target.value
                    setQDraft(v)
                    // Lab onKeywordChange — clear filter when box emptied.
                    if (!v.trim()) {
                      setPage(1)
                      setQ('')
                    }
                  }}
                  placeholder="Enter Player name and Search…"
                />
              )}
            </Field>
          </div>
          <Button type="submit" variant="secondary">
            Search
          </Button>
          <Button type="button" onClick={() => setAddOpen(true)}>
            Add cheater
          </Button>
        </form>

        {listQ.isError ? (
          <div className="cm-hint" role="alert">
            <p>{(listQ.error as Error).message}</p>
            <Button type="button" variant="secondary" onClick={() => void listQ.refetch()}>
              Retry
            </Button>
          </div>
        ) : null}

        <DataTable
          label="All cheaters list"
          density="compact"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={listQ.isFetching && !rows.length}
          empty={
            !listQ.isError ? (
              <div className="cm-security-empty">
                <StateBlock icon="search" title="Nothing here yet">
                  No cheaters flagged yet.
                </StateBlock>
              </div>
            ) : null
          }
        />

        {total > pageSize ? (
          <div className="cm-security-pager">
            <Button
              type="button"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="cm-hint">
              Page {page} / {totalPages} · {total} row(s)
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </div>

      <Modal
        open={addOpen}
        onClose={() => {
          if (addM.isPending) return
          setAddOpen(false)
          setPlayer(null)
          setReason('')
        }}
        title="Add cheater player"
        closeDisabled={addM.isPending}
        footer={
          <div className="cm-form-actions">
            <Button
              type="button"
              variant="secondary"
              disabled={addM.isPending}
              onClick={() => {
                setAddOpen(false)
                setPlayer(null)
                setReason('')
              }}
            >
              No
            </Button>
            <Button
              type="button"
              busy={addM.isPending}
              disabled={!player || !reason.trim()}
              onClick={() => addM.mutate()}
            >
              Add
            </Button>
          </div>
        }
      >
        <div className="cm-form">
          <CheaterPlayerPicker value={player} onChange={setPlayer} />
          <Field label="Comment" required>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Comment"
              />
            )}
          </Field>
        </div>
      </Modal>

      <Modal
        open={pendingOff != null}
        onClose={() => {
          if (removeM.isPending) return
          setPendingOff(null)
        }}
        title="Are you sure you want to remove from cheater list?"
        subtitle={pendingOff ? `Player: ${pendingOff.username}` : undefined}
        tone="danger"
        closeDisabled={removeM.isPending}
        footer={
          <div className="cm-form-actions">
            <Button
              type="button"
              variant="secondary"
              disabled={removeM.isPending}
              onClick={() => setPendingOff(null)}
            >
              No
            </Button>
            <Button
              type="button"
              variant="danger"
              busy={removeM.isPending}
              onClick={() => {
                if (pendingOff) removeM.mutate(pendingOff)
              }}
            >
              Yes
            </Button>
          </div>
        }
      >
        <p className="cm-hint">This turns the cheater flag off for this player.</p>
      </Modal>
    </CmPanel>
  )
}
